import React from "react";

import { useToolbarButtons } from "contexts/toolbars/hooks";

import { ButtonRenderer } from "./ButtonRenderer";
import { TOOLBAR_COLORS, TOOLBAR_CONTAINER } from "./constants";

type Alignment = "horizontal" | "vertical";
type Variant = "default" | "fly-out";
type FlyoutPlacement = "left" | "right";

interface ToolbarProps extends React.HTMLAttributes<HTMLDivElement> {
    alignment?: Alignment;
    variant?: Variant;
    flyoutPlacement?: FlyoutPlacement;
}

/**
 * Managed toolbar component using centralized state management.
 * Buttons must be registered via dispatch actions before rendering.
 */
export const Toolbar = ({
    alignment = "vertical",
    variant = "default",
    flyoutPlacement = "left",

    id,
    style,
    ...props
}: ToolbarProps) => {
    if (!id) {
        throw new Error("Toolbar component requires an 'id' prop for state management");
    }

    const visibleButtons = useToolbarButtons(id);

    if (visibleButtons.length === 0) {
        return null;
    }

    const baseStyle: React.CSSProperties = {
        display: "flex",
        flexDirection: alignment === "vertical" ? "column" : "row",
        pointerEvents: "auto",
        width: `${TOOLBAR_CONTAINER.WIDTH}px`,
        alignItems: "center",
    };

    const variantStyles: Record<Variant, React.CSSProperties> = {
        default: {
            padding: `${TOOLBAR_CONTAINER.PADDING}px`,
            gap: `${TOOLBAR_CONTAINER.GAP}px`,
            backgroundColor: TOOLBAR_CONTAINER.BG,
            borderRadius: `${TOOLBAR_CONTAINER.RADIUS}px`,
            border: TOOLBAR_CONTAINER.BORDER,
            boxShadow: TOOLBAR_CONTAINER.SHADOW,
        },
        "fly-out": {
            padding: `${TOOLBAR_CONTAINER.PADDING}px`,
            gap: `${TOOLBAR_CONTAINER.GAP}px`,
            boxShadow: TOOLBAR_CONTAINER.SHADOW,
        },
    };

    let dynamicFlyoutStyle: React.CSSProperties = {};
    if (variant === "fly-out") {
        if (flyoutPlacement === "left") {
            dynamicFlyoutStyle = {
                borderRadius: `${TOOLBAR_CONTAINER.RADIUS}px 0px ${TOOLBAR_CONTAINER.RADIUS}px ${TOOLBAR_CONTAINER.RADIUS}px`,
                background: `linear-gradient(90deg, ${TOOLBAR_COLORS.WHITE} 67.05%, ${TOOLBAR_COLORS.FLYOUT_GREY} 127.27%)`,
                marginRight: `${TOOLBAR_CONTAINER.RADIUS}px`,
            };
        } else {
            dynamicFlyoutStyle = {
                borderRadius: `0px ${TOOLBAR_CONTAINER.RADIUS}px ${TOOLBAR_CONTAINER.RADIUS}px ${TOOLBAR_CONTAINER.RADIUS}px`,
                background: `linear-gradient(-90deg, ${TOOLBAR_COLORS.WHITE} 67.05%, ${TOOLBAR_COLORS.FLYOUT_GREY} 127.27%)`,
                marginLeft: `${TOOLBAR_CONTAINER.RADIUS}px`,
            };
        }
    }

    const toolbarStyle: React.CSSProperties = {
        ...baseStyle,
        ...variantStyles[variant],
        ...style,
        ...dynamicFlyoutStyle,
    };

    return (
        <div role="toolbar" style={toolbarStyle} id={id} {...props}>
            <ButtonRenderer toolbarId={id} />
        </div>
    );
};
